'use client';

import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import useInvoiceUploadStore from '@/lib/invoice-upload/store/use-invoice-upload-store';
import { Button, Input, useDisclosure } from '@heroui/react';
import { useTranslations } from 'next-intl';
import ModalTemplate from './modal-template';
import { useIsMobile } from '@/app/hooks/use-is-mobile';
import Link from 'next/link';
import useCustomerStore from '../store/use-customer-store';

const logger = console;

export type InvoiceUploadRef = {
  open: () => void;
};

type InvoiceUploadProps = {
  children: React.ReactNode;
  autoOpen?: boolean;
};

const InvoiceUpload = forwardRef<InvoiceUploadRef, InvoiceUploadProps>(
  (
    { children, autoOpen }: InvoiceUploadProps,
    ref: ForwardedRef<InvoiceUploadRef>
  ) => {
    const analyse = useInvoiceUploadStore((state) => state.analyse);
    const setError = useInvoiceUploadStore((state) => state.setError);
    const { isOpen, onOpenChange, onOpen, onClose } = useDisclosure();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const t = useTranslations('invoiceUpload.consent');
    const linkStyles = 'underline font-semibold cursor-pointer';
    const error = useInvoiceUploadStore((state) => state.error);
    const isMobile = useIsMobile();
    const [isModalDismissable, setIsModalDismissable] = useState(true);
    const maxFileSize = Number(process.env.NEXT_PUBLIC_MAX_FILE_SIZE);
    const customer = useCustomerStore((state) => state.customer);
    const isLutz = customer.toLowerCase() == 'lutz';

    useImperativeHandle(ref, () => ({
      open: () => {
        onOpen();
      },
    }));

    useEffect(() => {
      if (autoOpen) {
        setIsModalDismissable(false);
        onOpen();
      }
    }, [autoOpen]);

    useEffect(() => {
      if (!isOpen && !isModalDismissable) {
        setIsModalDismissable(true);
      }
    }, [isOpen]);

    const manualEntryLink = (chunks: React.ReactNode) =>
      error ? (
        <Link className={linkStyles} href="/" data-testid="return-homepage">
          {chunks}
        </Link>
      ) : (
        <span
          data-testid="close-popups"
          className={linkStyles}
          onClick={onClose}
        >
          {chunks}
        </span>
      );

    function handleSelectFile(e: React.ChangeEvent<HTMLInputElement>): void {
      const files = e.target.files;
      if (!files) return;
      const selectedFiles = Array.from(files);
      const totalFileSize = selectedFiles
        .map((file) => file.size)
        .reduce((a, b) => a + b);

      if (totalFileSize > maxFileSize) {
        setError('Please limit file size to allowed maximum');
      } else if (!validateFile(selectedFiles)) {
        setError('Please upload a valid PDF or Image file.');
      } else {
        setError(null);
        processFile(selectedFiles);
      }

      resetInput();
      onClose();
    }

    async function processFile(files: File[]) {
      try {
        await analyse(files);
      } catch (err: unknown) {
        logger.error('Cannot process file', { error: err });
        setError('Invalid format.');
      }
    }

    function resetInput() {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }

    return (
      <span className="cursor-pointer">
        <span data-testid="invoice-upload-trigger" onClick={onOpen}>
          {children}
        </span>
        <ModalTemplate
          isOpen={isOpen}
          isDismissable={isModalDismissable}
          onOpenChange={onOpenChange}
          title={t('title')}
        >
          <p
            data-testid="invoice-upload-body"
            className="leading-6.5 text-base md:text-lg"
          >
            {t('body')}
          </p>
          <Button
            data-testid="invoice-upload-button"
            className={`btn-primary btn-padded my-8 ${isLutz && '!px-0'}`}
            onPress={() => fileInputRef.current?.click()}
          >
            <p data-testid="invoice-upload-button-text">
              {isMobile && isLutz ? (
                <>
                  <span
                    className="block"
                    data-testid="invoice-upload-button-text"
                  >
                    {t('expandedCtaUpload')}
                  </span>
                  <span className="block">{t('expandedCtaPrivacy')}</span>
                </>
              ) : isMobile ? (
                t('shortenedCta')
              ) : (
                t('cta')
              )}
            </p>
          </Button>
          <p data-testid="data-privacy-text" className="text-sm">
            {t.rich('dataPrivacy.title', {
              link: (chunks) => (
                <a
                  data-testid="data-privacy-link"
                  href={t('dataPrivacy.link')}
                  target="_blank"
                  className={linkStyles}
                >
                  {chunks}
                </a>
              ),
            })}
          </p>
          <p data-testid="manual-entry-text" className="pt-4 text-sm">
            {t.rich('manualEntry', {
              link: manualEntryLink,
            })}
          </p>
          <Input
            ref={fileInputRef}
            type="file"
            onChange={handleSelectFile}
            className="hidden"
            accept=".pdf,image/*"
            multiple
          />
        </ModalTemplate>
      </span>
    );
  }
);

function validateFile(files: File[]): boolean {
  return files.every(
    (file) => file.type === 'application/pdf' || file.type.startsWith('image/')
  );
}

InvoiceUpload.displayName = 'InvoiceUpload';
export default InvoiceUpload;
